import { useState, useEffect } from 'react';
import { X, MapPin, Calendar, Clock, Check, Loader2 } from 'lucide-react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar as CalendarPicker } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { format } from 'date-fns';
import axios from 'axios';
import { toast } from 'sonner';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix Leaflet icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function LocationMarker({ position, setPosition }) {
  useMapEvents({
    click(e) {
      setPosition([e.latlng.lat, e.latlng.lng]);
    },
  });

  return position ? <Marker position={position} /> : null;
}

const EnhancedBookingModal = ({ open, onClose, selectedService, initialData, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState([]);
  const [date, setDate] = useState(new Date());
  const [position, setPosition] = useState([6.5244, 3.3792]); // Lagos default
  
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    address: initialData?.address || '',
    service_id: selectedService?.id || '',
    service_name: selectedService?.name || '',
    booking_time: '10:00',
    location_type: 'home',
    notes: ''
  });

  useEffect(() => {
    if (open) {
      fetchServices();
      setStep(1);
      
      // Pre-fill with logged-in user data
      const savedUser = localStorage.getItem('barbergo_user');
      if (savedUser) {
        const user = JSON.parse(savedUser);
        setFormData(prev => ({
          ...prev,
          customer_name: user.name || '',
          customer_email: user.email || '',
          customer_phone: user.phone || ''
        }));
      }
    }
  }, [open]);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const bookingData = {
        ...formData,
        booking_date: format(date, 'yyyy-MM-dd'),
        latitude: position[0],
        longitude: position[1]
      };

      const response = await axios.post(`${API}/bookings`, bookingData);
      toast.success('Booking request sent! Waiting for barber to accept.');
      
      if (onSuccess) {
        onSuccess(response.data.id);
      }
      
      onClose();
      setFormData({
        customer_name: '',
        customer_phone: '',
        customer_email: '',
        address: '',
        service_id: '',
        service_name: '',
        booking_time: '10:00',
        location_type: 'home',
        notes: ''
      });
      setStep(1);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  const timeSlots = ['08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00'];

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-[var(--bg-secondary)] dark:bg-[#1A1A1A] border border-[var(--border-color)] rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-[var(--bg-secondary)] dark:bg-[#1A1A1A] border-b border-[var(--border-color)] p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-[var(--text-primary)]">Book Your Barber</h2>
              <div className="flex items-center gap-2 mt-2">
                {[1, 2, 3].map((s) => (
                  <div key={s} className={`h-2 flex-1 rounded-full ${s <= step ? 'bg-[#00C167]' : 'bg-gray-600'}`} />
                ))}
              </div>
            </div>
            <button onClick={onClose} className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Step 1: Service & Details */}
          {step === 1 && (
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-[var(--text-primary)]">Service Details</h3>
              
              <div>
                <Label>Your Name</Label>
                <Input
                  value={formData.customer_name}
                  onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                  className="mt-1"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <Label>Phone Number</Label>
                <Input
                  value={formData.customer_phone}
                  onChange={(e) => setFormData({...formData, customer_phone: e.target.value})}
                  className="mt-1"
                  placeholder="+234 800 000 0000"
                />
              </div>

              <div>
                <Label>Service</Label>
                <Select value={formData.service_id} onValueChange={(value) => {
                  const service = services.find(s => s.id === value);
                  setFormData({...formData, service_id: value, service_name: service?.name || ''});
                }}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select service" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map(service => (
                      <SelectItem key={service.id} value={service.id}>
                        {service.name} - ₦{service.price?.toLocaleString()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button onClick={() => setStep(2)} className="w-full bg-[#00C167] hover:bg-[#009E54]">
                Next: Choose Location
              </Button>
            </div>
          )}

          {/* Step 2: Location */}
          {step === 2 && (
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-[var(--text-primary)]">Confirm Location</h3>
              
              <div>
                <Label>Address</Label>
                <Textarea
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  className="mt-1"
                  rows={2}
                  placeholder="Enter your address"
                />
              </div>

              <div>
                <Label>Adjust pin on map (click to move)</Label>
                <div className="mt-2 h-64 rounded-lg overflow-hidden border border-[var(--border-color)]">
                  <MapContainer center={position} zoom={13} style={{ height: '100%', width: '100%' }}>
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    <LocationMarker position={position} setPosition={setPosition} />
                  </MapContainer>
                </div>
                <p className="text-xs text-[var(--text-secondary)] mt-1">Click map to adjust your exact location</p>
              </div>

              <div className="flex gap-2">
                <Button onClick={() => setStep(1)} variant="outline" className="flex-1">Back</Button>
                <Button onClick={() => setStep(3)} className="flex-1 bg-[#00C167] hover:bg-[#009E54]">Next: Date & Time</Button>
              </div>
            </div>
          )}

          {/* Step 3: Date & Time */}
          {step === 3 && (
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-[var(--text-primary)]">Schedule Booking</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full mt-1">
                        <Calendar className="mr-2 h-4 w-4" />
                        {format(date, 'PPP')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent><CalendarPicker mode="single" selected={date} onSelect={(d) => d && setDate(d)} /></PopoverContent>
                  </Popover>
                </div>

                <div>
                  <Label>Time</Label>
                  <Select value={formData.booking_time} onValueChange={(value) => setFormData({...formData, booking_time: value})}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {timeSlots.map(time => <SelectItem key={time} value={time}>{time}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Special Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="mt-1"
                  rows={2}
                  placeholder="Any special requests?"
                />
              </div>

              <div className="bg-[#00C167]/10 border border-[#00C167]/20 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Check className="w-5 h-5 text-[#00C167]" />
                  <span className="font-semibold text-[var(--text-primary)]">Ready to send request</span>
                </div>
                <p className="text-sm text-[var(--text-secondary)]">Barber will accept and provide ETA</p>
              </div>

              <div className="flex gap-2">
                <Button onClick={() => setStep(2)} variant="outline" className="flex-1">Back</Button>
                <Button onClick={handleSubmit} disabled={loading} className="flex-1 bg-[#00C167] hover:bg-[#009E54]">
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</> : 'Send Request'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnhancedBookingModal;